export const SEO_LANGUAGES = Object.freeze([
  { key: 'ua', label: 'UA' },
  { key: 'ru', label: 'RU' },
  { key: 'en', label: 'EN' }
])

export const SEO_PLACEHOLDERS = Object.freeze([
  '{userName}',
  '{courseTitle}',
  '{testTitle}',
  '{siteName}'
])

const THEMATIC_KEYWORDS = Object.freeze({
  course: {
    ua: ['курс', 'навчання', 'оптометрія'],
    ru: ['курс', 'обучение', 'оптометрия'],
    en: ['course', 'education', 'optometry']
  },
  event: {
    ua: ['подія', 'оптометрія', 'професійний розвиток'],
    ru: ['мероприятие', 'оптометрия', 'профессиональное развитие'],
    en: ['event', 'optometry', 'professional development']
  },
  team: {
    ua: ['експерт', 'викладач', 'оптометрія'],
    ru: ['эксперт', 'преподаватель', 'оптометрия'],
    en: ['expert', 'teacher', 'optometry']
  },
  blog: {
    ua: ['стаття', 'новини', 'оптометрія'],
    ru: ['статья', 'новости', 'оптометрия'],
    en: ['article', 'news', 'optometry']
  }
})

export function localizedValue(value, lang = 'ua') {
  if (value === undefined || value === null) return ''
  if (typeof value === 'object' && !Array.isArray(value)) {
    return String(value[lang] || value.ua || value.ru || value.en || '')
  }
  return String(value)
}

export function stripHtml(value) {
  return String(value || '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

export function summarizeText(value, maxLength = 170) {
  const text = stripHtml(value)
  if (text.length <= maxLength) return text
  const sliced = text.slice(0, maxLength + 1)
  const breakAt = sliced.lastIndexOf(' ')
  return `${sliced.slice(0, breakAt > 80 ? breakAt : maxLength).trim()}…`
}

function categoryNames(categoryValue, categories, lang) {
  const ids = Array.isArray(categoryValue) ? categoryValue : [categoryValue]
  return ids
    .filter(Boolean)
    .map(categoryId => {
      const category = (categories || []).find(item => item.id === categoryId)
      return category ? localizedValue(category.name || category.title, lang) : localizedValue(categoryId, lang)
    })
    .filter(Boolean)
}

function uniqueKeywords(values) {
  const seen = new Set()
  return values
    .flatMap(value => Array.isArray(value) ? value : [value])
    .map(value => String(value || '').trim())
    .filter(value => {
      const key = value.toLocaleLowerCase()
      if (!value || seen.has(key)) return false
      seen.add(key)
      return true
    })
    .join(', ')
}

function fallbackDescription(title, lang) {
  const prefix = {
    ua: 'Дізнайтеся більше про',
    ru: 'Узнайте больше о',
    en: 'Learn more about'
  }[lang] || 'Learn more about'
  return summarizeText(`${prefix} ${title}. Optometry Academy — професійна освіта, актуальні знання та розвиток у сфері оптометрії.`)
}

export function generateAutomaticSeo(type, entity, options = {}) {
  const lang = options.lang || 'ua'
  const categories = options.categories || []
  const thematic = (THEMATIC_KEYWORDS[type] && THEMATIC_KEYWORDS[type][lang]) || []
  const titleField = type === 'team' ? entity.name : entity.title
  const title = summarizeText(localizedValue(titleField, lang), 60)
  const categoryList = categoryNames(entity.category, categories, lang)

  let description = ''
  let keywords = []

  if (type === 'course') {
    description = localizedValue(entity.shortDescription, lang) ||
      localizedValue(entity.fullDescription, lang) ||
      localizedValue(entity.results, lang)
    keywords = [title, categoryList, thematic]
  } else if (type === 'event') {
    description = localizedValue(entity.description, lang)
    keywords = [title, categoryList, thematic]
  } else if (type === 'team') {
    description = localizedValue(entity.bio, lang) ||
      localizedValue(entity.position, lang) ||
      localizedValue(entity.role, lang)
    keywords = [
      title,
      localizedValue(entity.specialization || entity.speciality, lang),
      localizedValue(entity.position, lang),
      localizedValue(entity.role, lang),
      thematic
    ]
  } else if (type === 'blog') {
    description = localizedValue(entity.description, lang) || localizedValue(entity.body, lang)
    keywords = [
      title,
      categoryList,
      localizedValue(entity.authorName || entity.author, lang),
      thematic
    ]
  }

  return {
    title,
    description: summarizeText(description, 170) || fallbackDescription(title, lang),
    keywords: uniqueKeywords(keywords)
  }
}

export function resolveSeoPlaceholders(value, context = {}) {
  return SEO_PLACEHOLDERS.reduce((result, placeholder) => {
    const contextKey = placeholder.slice(1, -1)
    return result.split(placeholder).join(context[contextKey] || placeholder)
  }, String(value || ''))
}

export function createTagId() {
  return `tag_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}
